// ==============================================================================
// FILE: src/pages/AuditTrailPage.tsx (Updated for Detailed Change Display)
// ==============================================================================
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, CircularProgress, List, ListItem,
  ListItemText, ListItemIcon, Divider, Button, Alert, Grid
} from '@mui/material';
import { 
    ArrowBack, History as HistoryIcon, CheckCircle as ApproveIcon,
    Cancel as RejectIcon, Link as LinkIcon, Edit as EditIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import { useAppContext } from '../contexts/AppContext';
import { AuditLog } from '../types';

const getActionIcon = (action: string) => {
    if (action.includes('approve')) return <ApproveIcon color="success" sx={{ mr: 2 }} />;
    if (action.includes('reject')) return <RejectIcon color="error" sx={{ mr: 2 }} />;
    if (action.includes('link')) return <LinkIcon color="primary" sx={{ mr: 2 }} />;
    if (action.includes('correction')) return <SaveIcon color="info" sx={{ mr: 2 }} />;
    return <HistoryIcon sx={{ mr: 2 }} />;
};

// --- NEW: Component to render the detailed changes ---
const ChangeLogDetail = ({ changes }: { changes: any }) => {
    if (!changes || Object.keys(changes).length === 0) {
        return null;
    }

    // A helper to format the field names nicely
    const formatFieldName = (field: string) => {
        return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    return (
        <Box component={Paper} variant="outlined" sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Changes:</Typography>
            {Object.entries(changes).map(([field, value]: [string, any]) => (
                <Typography key={field} variant="body2" color="text.secondary" component="div">
                    - {formatFieldName(field)}: <del>{value.old || 'N/A'}</del> → <strong>{value.new || 'N/A'}</strong>
                </Typography>
            ))}
        </Box>
    );
};


export default function AuditTrailPage({ batchId }: { batchId: string }): JSX.Element {
  const { apiService, showNotification, setActivePage, setAuditBatchId } = useAppContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const logs = await apiService.getAuditLogs(batchId);
      setAuditLogs(logs);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch audit trail.';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [apiService, batchId, showNotification]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleBack = () => {
    setAuditBatchId(null);
    setActivePage('Claim History');
  };

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }} elevation={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack}>
          Back to Claim History
        </Button>
      </Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
        Audit Trail
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Batch ID: {batchId}
      </Typography>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error">{error}</Alert>}
      
      {!loading && !error && (
        <List>
          {auditLogs.map((log, index) => (
            <React.Fragment key={log.id}>
              <ListItem alignItems="flex-start">
                <ListItemIcon>{getActionIcon(log.action_type)}</ListItemIcon>
                <ListItemText
                  primary={`${log.user_name} ${log.action_type.replace(/_/g, ' ')} ${log.claim_item_description ? `"${log.claim_item_description}"` : ''}`}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block' }}>
                        {new Date(log.created_at).toLocaleString()}
                      </Typography>
                      {log.notes && `Notes: "${log.notes}"`}
                      {/* --- CHANGE: Render the detailed change log component --- */}
                      <ChangeLogDetail changes={log.details?.changes} />
                    </>
                  }
                />
              </ListItem>
              {index < auditLogs.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Paper>
  );
}
